import React, { useMemo } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import LogoutIcon from "@mui/icons-material/Logout";

/* -------------------------------------------------------------------------- */
/*  Design tokens – one place to tweak the whole component                    */
/* -------------------------------------------------------------------------- */

const MUTED = "#92a9a9";
const DIVIDER = "rgba(146, 169, 169, 0.2)";
const DIVIDER_STRONG = "rgba(146, 169, 169, 0.35)";
const MONO_FONT = '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace';
const INDICATOR_WIDTH = 4; // left status bar on rows / cards
const RADIUS = 5; // matches profile cards (theme spacing × default radius)

/**
 * Spacing rhythm. Everything is a multiple of 8px:
 *   8px  = tight  (between related lines)
 *   16px = base   (between groups)
 *   24px = roomy  (outer padding / gap between sections)
 * Values are MUI spacing units (1 = 8px by default).
 */
const SPACE = {
    tight: 1,
    base: 2,
    roomy: 3,

    sectionBottom: 3,
    tableWrapY: 0.5,
    cellX: 3,
    cellY: 2.5,
    headerY: 2,
    cardX: 2.5,
    cardY: 2.5,
    cardGap: 2,
    detailRowY: 1.25,
    detailGap: 2,
    statusBlockMb: 1.5,
};

// Type scale (px). Only these sizes are used below.
const FS = {
    title: 16,
    body: 14,
    small: 13,
    caption: 12,
    label: 11,
};

/**
 * Desktop table columns. Single source of truth for headers AND widths,
 * so the header row and body cells can never drift out of sync again.
 * Widths add up to 100%.
 */
const TABLE_COLUMNS = [
    { key: "session", label: "Session", width: "18%" },
    { key: "location", label: "Login Device", width: "28%" },
    { key: "ip", label: "IP address", width: "18%" },
    { key: "location", label: "Login location", width: "28%" },
    { key: "loginTime", label: "Login time", width: "18%" },
    { key: "lastVisit", label: "Last visit", width: "18%" },
];

const EMPTY_VALUES = ["", "—", "-", "unknown"];

const isEmpty = (value) =>
    value === null ||
    value === undefined ||
    EMPTY_VALUES.includes(String(value).trim().toLowerCase());

/** Renders a value, or a quiet dash when the backend sends nothing useful. */
function Value({ value, mono = false, strong = false }) {
    if (isEmpty(value)) {
        return (
            <Typography
                component="span"
                sx={{ fontSize: FS.small, color: alpha(MUTED, 0.7) }}
            >
                —
            </Typography>
        );
    }

    return (
        <Typography
            component="span"
            sx={{
                fontSize: mono ? FS.caption : FS.body,
                fontWeight: strong ? 600 : 400,
                lineHeight: 1.5,
                fontFamily: mono ? MONO_FONT : "inherit",
                fontVariantNumeric: "tabular-nums",
                wordBreak: "break-word",
                color: "text.primary",
            }}
        >
            {value}
        </Typography>
    );
}

const HEADER_CELL_SX = {
    color: MUTED,
    fontSize: FS.label,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    lineHeight: 1.5,
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${DIVIDER_STRONG}`,
    px: SPACE.cellX,
    py: SPACE.headerY,
    verticalAlign: "bottom",
};

const BODY_CELL_SX = {
    fontSize: FS.body,
    lineHeight: 1.55,
    px: SPACE.cellX,
    py: SPACE.cellY,
    verticalAlign: "top",
    wordBreak: "break-word",
    borderBottom: `1px solid ${DIVIDER}`,
};

function sortSessionsCurrentFirst(sessions) {
    if (!sessions?.length) return [];
    return [...sessions].sort((a, b) => {
        if (Boolean(a.is_current) === Boolean(b.is_current)) return 0;
        return a.is_current ? -1 : 1;
    });
}

/* -------------------------------------------------------------------------- */
/*  "You are signed in on this device" callout                                */
/*  Currently NOT rendered (see the commented-out usage in the main export).  */
/*  Uncomment that usage to bring it back.                                    */
/* -------------------------------------------------------------------------- */

function CurrentSessionCallout({ session }) {
    const theme = useTheme();
    const success = theme.palette.success.main;
    const showLocation = !isEmpty(session.active_location ?? session.location);
    const showIp = !isEmpty(session.location_ip ?? session.ip_address);

    return (
        <Box
            sx={{
                mb: SPACE.sectionBottom,
                borderRadius: RADIUS,
                border: `1px solid ${alpha(success, 0.45)}`,
                backgroundColor: alpha(success, 0.12),
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: SPACE.base,
                    padding: theme.spacing(SPACE.roomy),
                    [theme.breakpoints.down("sm")]: {
                        padding: theme.spacing(SPACE.base),
                        gap: SPACE.tight * 1.5,
                    },
                }}
            >
                <CheckCircleIcon
                    aria-hidden
                    sx={{
                        color: "success.main",
                        fontSize: 28,
                        flexShrink: 0,
                        // Optically centre the 28px icon on the first title line (24px tall)
                        mt: "-2px",
                    }}
                />

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    {/* Title row: title left, chip right, both centred on one line */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            columnGap: SPACE.base,
                            rowGap: SPACE.tight,
                            minHeight: 24,
                        }}
                    >
                        <Typography
                            role="status"
                            sx={{
                                fontSize: FS.title,
                                fontWeight: 600,
                                lineHeight: "24px",
                            }}
                        >
                            You are signed in on this device
                        </Typography>

                        <Chip
                            label="This device"
                            size="small"
                            variant="outlined"
                            sx={{
                                flexShrink: 0,
                                height: 24,
                                fontWeight: 700,
                                fontSize: FS.label,
                                letterSpacing: "0.03em",
                                color: "success.main",
                                borderColor: alpha(success, 0.6),
                                bgcolor: alpha(success, 0.18),
                                "& .MuiChip-label": { px: 1.25 },
                            }}
                        />
                    </Box>

                    {/* Device + location: tight, related lines */}
                    <Stack spacing={0.5} sx={{ mt: SPACE.tight }}>
                        <Typography
                            sx={{
                                fontSize: FS.body,
                                color: "text.secondary",
                                lineHeight: 1.5,
                            }}
                        >
                            {session.login_device}
                        </Typography>
                        {showLocation && (
                            <Typography
                                sx={{
                                    fontSize: FS.body,
                                    color: "text.secondary",
                                    lineHeight: 1.5,
                                }}
                            >
                                {session.active_location ?? session.location}
                            </Typography>
                        )}
                    </Stack>

                    {/* Meta line: separated by one divider, 16px above and below */}
                    <Box
                        sx={{
                            mt: SPACE.base,
                            pt: SPACE.base,
                            borderTop: `1px solid ${alpha(success, 0.25)}`,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: FS.caption,
                                color: MUTED,
                                lineHeight: 1.5,
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            Last activity: {session.last_visit}
                            {showIp ? (
                                <>
                                    <Box
                                        component="span"
                                        sx={{ mx: 1, opacity: 0.5 }}
                                        aria-hidden
                                    >
                                        ·
                                    </Box>
                                    IP{" "}
                                    {session.location_ip ?? session.ip_address}
                                </>
                            ) : null}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

/* -------------------------------------------------------------------------- */
/*  Shared pieces                                                             */
/* -------------------------------------------------------------------------- */

function SessionStatusLabel({ isCurrent }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.25}>
            {isCurrent ? (
                <CheckCircleIcon
                    sx={{ fontSize: 18, color: "success.main", flexShrink: 0 }}
                    aria-hidden
                />
            ) : (
                <DevicesOtherIcon
                    sx={{ fontSize: 18, color: MUTED, flexShrink: 0 }}
                    aria-hidden
                />
            )}
            <Typography
                component="span"
                sx={{
                    fontSize: FS.small,
                    lineHeight: 1.4,
                    fontWeight: isCurrent ? 700 : 500,
                    color: isCurrent ? "success.main" : MUTED,
                    whiteSpace: "nowrap",
                }}
            >
                {isCurrent ? "This device" : "Other session"}
            </Typography>
        </Stack>
    );
}

/* -------------------------------------------------------------------------- */
/*  Compact (mobile / tablet) card layout                                     */
/* -------------------------------------------------------------------------- */

function SessionDetailRow({ label, value, mono }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "112px 1fr", sm: "140px 1fr" },
                columnGap: SPACE.detailGap,
                alignItems: "baseline",
                py: SPACE.detailRowY,
                borderBottom: `1px solid ${alpha(MUTED, 0.15)}`,
                "&:last-of-type": { borderBottom: "none", pb: 0 },
            }}
        >
            <Typography
                sx={{
                    fontSize: FS.label,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    lineHeight: 1.4,
                    color: MUTED,
                    textTransform: "uppercase",
                }}
            >
                {label}
            </Typography>
            <Value value={value} mono={mono} />
        </Box>
    );
}

function SessionCard({ row, theme }) {
    const isCurrent = Boolean(row.is_current);

    return (
        <Box
            sx={{
                borderRadius: RADIUS,
                pl: `calc(${theme.spacing(
                    SPACE.cardX,
                )} + ${INDICATOR_WIDTH}px)`,
                pr: SPACE.cardX,
                py: SPACE.cardY,
                mb: SPACE.cardGap,
                "&:last-of-type": { mb: 0 },
                bgcolor: isCurrent
                    ? alpha(theme.palette.success.main, 0.1)
                    : alpha(theme.palette.common.white, 0.03),
                boxShadow: isCurrent
                    ? `inset ${INDICATOR_WIDTH}px 0 0 ${theme.palette.success.main}`
                    : `inset ${INDICATOR_WIDTH}px 0 0 ${alpha(MUTED, 0.25)}`,
            }}
        >
            <Box sx={{ mb: SPACE.statusBlockMb }}>
                <SessionStatusLabel isCurrent={isCurrent} />
            </Box>
            <SessionDetailRow label="Login device" value={row.login_device} />
            <SessionDetailRow
                label="IP address"
                value={row.location_ip ?? row.ip_address}
                mono
            />
            <SessionDetailRow
                label="Login location"
                value={row.active_location ?? row.location}
            />
            <SessionDetailRow label="Login time" value={row.login_time} />
            <SessionDetailRow label="Last visit" value={row.last_visit} />
        </Box>
    );
}

/* -------------------------------------------------------------------------- */
/*  Desktop table layout                                                      */
/* -------------------------------------------------------------------------- */

function SessionsTable({ orderedSessions, theme }) {
    const firstCellPl = `calc(${INDICATOR_WIDTH}px + ${theme.spacing(
        SPACE.cellX,
    )})`;
    const lastCellPr = theme.spacing(SPACE.cellX + 0.5);

    return (
        <Box
            sx={{
                width: "100%",
                mt: SPACE.tableWrapY,
                borderRadius: RADIUS,
                border: `1px solid ${DIVIDER}`,
                overflow: "hidden",
                bgcolor: alpha(theme.palette.common.white, 0.02),
            }}
        >
            <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                <Table
                    size="medium"
                    aria-label="Active account sessions"
                    sx={{
                        width: "100%",
                        minWidth: 960,
                        tableLayout: "fixed",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {TABLE_COLUMNS.map((col, i) => (
                                <TableCell
                                    key={col.key}
                                    sx={{
                                        ...HEADER_CELL_SX,
                                        width: col.width,
                                        ...(i === 0 && { pl: firstCellPl }),
                                        ...(i === TABLE_COLUMNS.length - 1 && {
                                            pr: lastCellPr,
                                        }),
                                    }}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {orderedSessions.map((row, index) => {
                            const isCurrent = Boolean(row.is_current);
                            const isLast = index === orderedSessions.length - 1;

                            return (
                                <TableRow
                                    key={row.session_id}
                                    sx={{
                                        bgcolor: isCurrent
                                            ? alpha(
                                                  theme.palette.success.main,
                                                  0.08,
                                              )
                                            : alpha(
                                                  theme.palette.common.white,
                                                  0.02,
                                              ),
                                        boxShadow: isCurrent
                                            ? `inset ${INDICATOR_WIDTH}px 0 0 ${theme.palette.success.main}`
                                            : `inset ${INDICATOR_WIDTH}px 0 0 ${alpha(
                                                  MUTED,
                                                  0.25,
                                              )}`,
                                        "& td": {
                                            ...(isLast && {
                                                borderBottom: "none",
                                            }),
                                        },
                                        "& td:first-of-type": {
                                            pl: firstCellPl,
                                        },
                                        "& td:last-of-type": {
                                            pr: lastCellPr,
                                        },
                                    }}
                                >
                                    {/* Session */}
                                    <TableCell sx={BODY_CELL_SX}>
                                        <SessionStatusLabel
                                            isCurrent={isCurrent}
                                        />
                                    </TableCell>

                                    {/* Login Device */}
                                    <TableCell sx={BODY_CELL_SX}>
                                        <Value value={row.login_device} />
                                    </TableCell>

                                    {/* IP address */}
                                    <TableCell sx={BODY_CELL_SX}>
                                        <Value
                                            value={
                                                row.location_ip ??
                                                row.ip_address
                                            }
                                            mono
                                        />
                                    </TableCell>

                                    {/* Login location */}
                                    <TableCell sx={BODY_CELL_SX}>
                                        <Value
                                            value={
                                                row.active_location ??
                                                row.location
                                            }
                                            strong={isCurrent}
                                        />
                                    </TableCell>

                                    {/* Login time */}
                                    <TableCell sx={BODY_CELL_SX}>
                                        <Value value={row.login_time} />
                                    </TableCell>

                                    {/* Last visit */}
                                    <TableCell sx={BODY_CELL_SX}>
                                        <Value value={row.last_visit} />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

/* -------------------------------------------------------------------------- */
/*  Main export                                                               */
/* -------------------------------------------------------------------------- */

export default function ActiveSessionsList({
    sessions,
    loading,
    error,
    onLogoutOtherSessions,
    logoutOtherSessionsLoading = false,
}) {
    const theme = useTheme();
    const isCompactLayout = useMediaQuery(theme.breakpoints.down("laptop"));

    const orderedSessions = useMemo(
        () => sortSessionsCurrentFirst(sessions),
        [sessions],
    );

    const hasOtherSessions = orderedSessions.some((row) => !row.is_current);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography
                role="alert"
                sx={{ fontSize: FS.body, color: "error.main", py: 2 }}
            >
                {error}
            </Typography>
        );
    }

    if (!orderedSessions.length) {
        return (
            <Typography
                sx={{ fontSize: FS.body, color: "text.secondary", py: 2 }}
            >
                No active sessions found.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: "100%", pt: 0.5, pb: 1 }}>
            {/* Top "You are signed in on this device" callout — use table Session column instead.
            {currentSession && (
                <CurrentSessionCallout session={currentSession} />
            )}
            */}

            {hasOtherSessions && onLogoutOtherSessions && (
                <Box
                    sx={{
                        mb: SPACE.roomy,
                        p: SPACE.base,
                        borderRadius: RADIUS,
                        border: `1px solid ${alpha(
                            theme.palette.warning.main,
                            0.55,
                        )}`,
                        bgcolor: alpha(theme.palette.warning.main, 0.12),
                    }}
                >
                    <Stack
                        direction={{ mobile: "column", tablet: "row" }}
                        spacing={SPACE.base}
                        alignItems={{ mobile: "stretch", tablet: "center" }}
                        justifyContent="space-between"
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: FS.body,
                                    fontWeight: 700,
                                    lineHeight: 1.45,
                                    color: "text.primary",
                                }}
                            >
                                Signed in elsewhere?
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: FS.small,
                                    lineHeight: 1.5,
                                    color: "text.secondary",
                                    mt: 0.5,
                                }}
                            >
                                End every other active session in one step. This
                                device stays signed in.
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            color="warning"
                            size="large"
                            fullWidth={isCompactLayout}
                            disabled={logoutOtherSessionsLoading}
                            onClick={onLogoutOtherSessions}
                            startIcon={
                                logoutOtherSessionsLoading ? (
                                    <CircularProgress
                                        size={20}
                                        color="inherit"
                                        aria-hidden
                                    />
                                ) : (
                                    <LogoutIcon />
                                )
                            }
                            sx={{
                                flexShrink: 0,
                                fontWeight: 700,
                                fontSize: FS.body,
                                textTransform: "none",
                                py: 1.25,
                                px: 3,
                                boxShadow: `0 4px 14px ${alpha(
                                    theme.palette.warning.main,
                                    0.35,
                                )}`,
                                "&:hover": {
                                    boxShadow: `0 6px 18px ${alpha(
                                        theme.palette.warning.main,
                                        0.45,
                                    )}`,
                                },
                            }}
                        >
                            {logoutOtherSessionsLoading
                                ? "Signing out…"
                                : "Log out other sessions"}
                        </Button>
                    </Stack>
                </Box>
            )}

            {isCompactLayout ? (
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {orderedSessions.map((row) => (
                        <SessionCard
                            key={row.session_id}
                            row={row}
                            theme={theme}
                        />
                    ))}
                </Box>
            ) : (
                <SessionsTable
                    orderedSessions={orderedSessions}
                    theme={theme}
                />
            )}
        </Box>
    );
}
